import { makeStyles, Typography } from "@material-ui/core";
import BigNumber from "bignumber.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getNetworkContractAddresses } from "src/config/addresses";
import { NETWORK } from "src/config/constants";
import { usePoolMonitoringContext } from "src/context/monitoring/poolMonitoringContext";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import {
  base27,
  encodePackageData,
  getContractEditPool,
  getContractFactoryRegistry,
  getContractGenerateInterestRateStrategyAddress,
} from "src/hooks/useContractEditPool";
import useLowerAddressParam from "src/hooks/useLowerAddressParam";
import useNotify from "src/hooks/useNotify";
import { useImmer } from "use-immer";
import BaseDialog from "../components/BaseDialog";
import StyledTextField from "../components/StyledTextField";
import { onTextChange } from "../components/util";

const useStyle = makeStyles((theme) => ({
  text: {
    color: theme.palette.text.hint,
    marginBottom: 10,
  },
}));

const defaultConfig = { fullWidth: true, type: "number" };

export default function InterestRateStrategyDialog(props) {
  const { asset, open, onClose } = props;
  const { pool } = useLowerAddressParam();
  const history = useHistory();
  const cls = useStyle();
  const mounted = useRef(false);
  const { currentProvider, wallet } = useProviderContext();
  const { data } = usePoolMonitoringContext();
  const param = useParams();
  const contractAddress = getNetworkContractAddresses(NETWORK.BSC_TESTNET);
  const [loading, setLoading] = useState(false);
  const { errorNotify, successNotify } = useNotify();

  const [value, setValue] = useImmer({
    utilizationOptimal: asset.utilizationOptimal,
    slope1: asset.slope1,
    slope2: asset.slope2,
    baseInterestRate: asset.baseBorrowRate,
  });
  const [helperText, setHelperText] = useImmer({
    utilizationOptimal: null,
    slope1: null,
    slope2: null,
    baseInterestRate: null,
  });

  const originData = useMemo(() => {
    return {
      utilizationOptimal: asset.utilizationOptimal,
      slope1: asset.slope1,
      slope2: asset.slope2,
      baseInterestRate: asset.baseBorrowRate,
    };
  }, [asset]);

  const isDisable =
    originData.utilizationOptimal == value.utilizationOptimal &&
    originData.slope1 == value.slope1 &&
    originData.slope2 == value.slope2 &&
    originData.baseInterestRate == value.baseInterestRate;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const contractFactoryRegistry = getContractFactoryRegistry(currentProvider);
      const interestRateStrategyFactory = await contractFactoryRegistry.getInterestRateFactory();
      const _contract = getContractGenerateInterestRateStrategyAddress(wallet.signer, interestRateStrategyFactory);
      const _promise = await _contract.initInterestRateStrategy(
        contractAddress.ADDRESS_PROVIDER_FACTORY,
        data[param.pool].overview.providerId,
        [
          [
            asset.address,
            new BigNumber(value.utilizationOptimal).multipliedBy(base27).toFixed(0),
            new BigNumber(value.baseInterestRate).multipliedBy(base27).toFixed(0),
            new BigNumber(value.slope1).multipliedBy(base27).toFixed(0),
            new BigNumber(value.slope2).multipliedBy(base27).toFixed(0),
          ],
        ]
      );
      await _promise.wait();
      const rateStrategyAddress = await _contract.getListInterestRateAddress(data[param.pool].overview.providerId, [
        asset.address,
      ]);
      const encodeData = await encodePackageData(currentProvider, "setReserveInterestRateStrategyAddress", [
        asset.address,
        rateStrategyAddress[0],
      ]);
      const poolUpdateControl = getContractEditPool(wallet.signer);
      const tx = await poolUpdateControl.submitTransaction(data[param.pool].overview.providerId, encodeData);
      await tx.wait();
      successNotify("Transaction Successfully");
      history.push(`/pool-monitoring/${pool}?action=pending`);
      window.location.reload(false);
    } catch (error) {
      console.error(error);
      errorNotify(error?.message);
    } finally {
      setLoading(false);
    }
  };

  function validate(key, value) {
    const _value = value[key];
    const _number = Number(_value);
    if (_value === null || _value === "") {
      setHelperText((draft) => {
        draft[key] = "This field is required";
      });
    } else if (_number < 0) {
      setHelperText((draft) => {
        draft[key] = "This field must be greater than 0";
      });
    } else
      setHelperText((draft) => {
        draft[key] = null;
      });
  }

  useEffect(() => {
    if (mounted.current) {
      validate("utilizationOptimal", value);
      validate("slope1", value);
      validate("slope2", value);
      validate("baseInterestRate", value);
    }
    mounted.current = true;
  }, [value]);

  const isInvalid = useMemo(() => {
    return (
      helperText["utilizationOptimal"] !== null ||
      helperText["slope1"] !== null ||
      helperText["slope2"] !== null ||
      helperText["baseInterestRate"] !== null
    );
  }, [helperText]);

  return (
    <BaseDialog
      butProps={{ disabled: isInvalid || isDisable || loading, onClick: handleConfirm }}
      assetName={asset.symbol}
      assetIcon={asset.image}
      {...{ open, onClose, loading }}
    >
      <Typography variant="h2" className={cls.text}>
        Interest Rate Strategy
      </Typography>
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Utilization Optimal",
          value: value.utilizationOptimal,
          onChange: (e) => onTextChange("utilizationOptimal", e.target.value, setValue),
          error: helperText.utilizationOptimal != null,
          helperText: helperText.utilizationOptimal,
        }}
        rootProps={{ mb: 2 }}
        info="Each token in each Pool has a utilization rate at each time, which is defined by the ratio that represents total borrows and represents total liquidity. This ratio regulates how much of a deposited token is used in the market."
      />
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Slope 1",
          value: value.slope1,
          onChange: (e) => onTextChange("slope1", e.target.value, setValue),
          error: helperText.slope1 != null,
          helperText: helperText.slope1,
        }}
        rootProps={{ mb: 2 }}
        info="Constant representing the scaling for the interest rate versus the utilisation, when U < U Optimal"
      />
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Slope 2",
          value: value.slope2,
          onChange: (e) => onTextChange("slope2", e.target.value, setValue),
          error: helperText.slope2 != null,
          helperText: helperText.slope2,
        }}
        rootProps={{ mb: 2 }}
        info="Constant representing the scaling for the interest rate versus the utilisation, when U >= U Optimal"
      />
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Base Interest Rate",
          value: value.baseInterestRate,
          onChange: (e) => onTextChange("baseInterestRate", e.target.value, setValue),
          error: helperText.baseInterestRate != null,
          helperText: helperText.baseInterestRate,
        }}
        rootProps={{ mb: 2 }}
        info="An interest rate is the amount of interest due per period as a proportion of the amount lent, deposited, or borrowed. In the Trava Lending pool, the interest rate is designed to control liquidity risk while also maximizing utilization."
      />
    </BaseDialog>
  );
}
